import type { ScenarioSettingsOut, ScenarioSettingsUpdate } from "../lib/api";

const levels = ["beginner", "intermediate", "advanced"] as const;
const tiers = ["fail", "partial", "pass", "bonus"] as const;
const emptyTier = () => ({ fail: "", partial: "", pass: "", bonus: "" });
const key = (m: { assessment_id: string; measure_id: string; label: string }) =>
  JSON.stringify([m.assessment_id, m.measure_id, m.label]);

export function TeamScenarioFields({ value, data, onChange, disabled }: {
  value: ScenarioSettingsUpdate;
  data: ScenarioSettingsOut;
  onChange: (next: ScenarioSettingsUpdate) => void;
  disabled: boolean;
}) {
  const settings = value.settings;
  const selected = settings.success_measure ? key(settings.success_measure) : "";
  const missing = selected && !data.options.some((o) => key(o) === selected);
  const contextLabel = (option: ScenarioSettingsOut["options"][number]) =>
    `${option.assessment_name} / ${option.measure_name} / ${option.label}`;
  const labelCounts = new Map<string, number>();
  for (const option of data.options) {
    const label = contextLabel(option);
    labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
  }
  const optionLabel = (option: ScenarioSettingsOut["options"][number]) => {
    const label = contextLabel(option);
    if (labelCounts.get(label) === 1) return label;
    const shortId = (id: string) => id.replaceAll("-", "").slice(-6);
    return `${label} (${shortId(option.assessment_id)}:${shortId(option.measure_id)})`;
  };
  return <fieldset disabled={disabled} className="space-y-3 rounded border border-border p-4">
    <legend className="px-1 font-semibold">Scenarios</legend>
    <p className="text-xs text-muted-foreground">
      Business rules only. Export format, privacy, evidence grounding and statistics cannot be overridden.
      Save assessment assignments before selecting a newly assigned measure.
    </p>
    {data.setup_required && <p role="status" className="text-sm">{data.setup_required}</p>}
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={value.enabled}
        onChange={(e) => onChange({ ...value, enabled: e.target.checked })} />
      Enable scenarios
    </label>
    <label className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">Success measure
      <select className="mt-1 block w-full rounded border border-input bg-card p-2 text-sm font-normal normal-case tracking-normal text-foreground" value={selected}
        onChange={(e) => {
          const option = data.options.find((o) => key(o) === e.target.value);
          onChange({ ...value, settings: { ...settings, success_measure: option ? {
            assessment_id: option.assessment_id, measure_id: option.measure_id, label: option.label,
          } : null } });
        }}>
        <option value="">Select an assigned detection label</option>
        {missing && <option value={selected}>Unavailable selection — choose another</option>}
        {data.options.map((o) => <option key={key(o)} value={key(o)}>
          {optionLabel(o)}
        </option>)}
      </select>
    </label>
    <label className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">Team scenario instructions
      <textarea rows={5} maxLength={8000} value={settings.instructions}
        className="mt-1 block w-full rounded border border-input bg-card p-2 text-sm font-normal normal-case tracking-normal text-foreground"
        onChange={(e) => onChange({ ...value, settings: { ...settings, instructions: e.target.value } })} />
    </label>
    {levels.map((level) => <fieldset key={level} className="space-y-2 rounded border border-border p-3">
      <legend className="capitalize px-1">{level}</legend>
      {tiers.map((tier) => <label key={tier} className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span className="capitalize">{level}</span> {tier.toUpperCase()}
        <textarea rows={2} maxLength={1500} value={settings.criteria?.[level][tier] ?? ""}
          className="mt-1 block w-full rounded border border-input bg-card p-2 text-sm font-normal normal-case tracking-normal text-foreground"
          onChange={(e) => {
            const criteria = settings.criteria ?? {
              beginner: emptyTier(), intermediate: emptyTier(), advanced: emptyTier(),
            };
            onChange({ ...value, settings: { ...settings, criteria: {
              ...criteria, [level]: { ...criteria[level], [tier]: e.target.value },
            } } });
          }} />
      </label>)}
    </fieldset>)}
  </fieldset>;
}
